import React from 'react';
import { Header, Footer, SEOHead } from '../components';
import { useSEO } from '../hooks/useSEO';
import { pageConfigs } from '../utils/seoConfig';

const AccessibilityPage: React.FC = () => {
  useSEO(pageConfigs.accessibility);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead config={pageConfigs.accessibility} />
      <Header />

      <section className="pt-40 section-padding bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto mb-12">
            <h1 className="text-4xl lg:text-5xl font-display font-bold text-slate-900 mb-6">
              Accessibility Statement
            </h1>
            <p className="text-lg text-slate-600">
              Last updated: November 2024
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom max-w-4xl">
          <div className="prose prose-lg max-w-none text-slate-600 space-y-8">
            
            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                1. Commitment to Accessibility
              </h2>
              <p>
                3 Arrows Media is committed to ensuring that our website and digital services are accessible to all individuals, including those with disabilities. We strive to provide an inclusive online experience for everyone and comply with the Web Content Accessibility Guidelines (WCAG) 2.1 at Level AA standards.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                2. Accessibility Features
              </h2>
              <p>Our website includes the following accessibility features:</p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li>Keyboard navigation support for all interactive elements</li>
                <li>Alt text for all images describing their content and purpose</li>
                <li>Semantic HTML markup for proper document structure</li>
                <li>Color contrast ratios that meet WCAG AA standards</li>
                <li>Resizable text and responsive design that works across all devices</li>
                <li>Form labels and instructions for all input fields</li>
                <li>Skip links to bypass repetitive content</li>
                <li>Support for screen readers and assistive technologies</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                3. Accessibility Standards
              </h2>
              <p>
                We aim to meet or exceed the Web Content Accessibility Guidelines (WCAG) 2.1 level AA standards. These guidelines help make web content more accessible to people with disabilities, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li>Visual impairments (blindness, low vision, color blindness)</li>
                <li>Hearing impairments (deafness, hard of hearing)</li>
                <li>Motor impairments (difficulty using mouse, tremors)</li>
                <li>Cognitive impairments (dyslexia, memory loss)</li>
                <li>Neurological impairments (epilepsy, autism)</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                4. Browser and Assistive Technology Support
              </h2>
              <p>
                Our website is compatible with the latest versions of major web browsers including Chrome, Firefox, Safari, and Edge. We also support popular assistive technologies including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li>NVDA (NonVisual Desktop Access)</li>
                <li>JAWS (Job Access With Speech)</li>
                <li>Voice Over (macOS and iOS)</li>
                <li>Narrator (Windows)</li>
                <li>Speech recognition software</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                5. Known Accessibility Issues
              </h2>
              <p>
                While we strive for full accessibility, some third-party content or embedded media may not be fully accessible. We are actively working to resolve these issues and appreciate your patience.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                6. Keyboard Navigation
              </h2>
              <p>
                Our website is fully navigable using keyboard alone. You can:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li><strong>Tab:</strong> Move forward through interactive elements</li>
                <li><strong>Shift + Tab:</strong> Move backward through interactive elements</li>
                <li><strong>Enter:</strong> Activate buttons and submit forms</li>
                <li><strong>Space:</strong> Activate checkboxes and toggle buttons</li>
                <li><strong>Arrow Keys:</strong> Navigate within menus and dropdowns</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                7. Report Accessibility Issues
              </h2>
              <p>
                If you encounter any accessibility barriers while using our website, please let us know. We welcome your feedback and are committed to making improvements:
              </p>
              <div className="mt-4 bg-slate-50 p-6 rounded-lg">
                <p><strong>Accessibility Coordinator</strong></p>
                <p>Email: accessibility@3arrowsmedia.com</p>
                <p className="mt-4 text-sm">Please include details about:</p>
                <ul className="list-disc pl-6 space-y-1 mt-2 text-sm">
                  <li>What accessibility issue you encountered</li>
                  <li>Which page or feature is affected</li>
                  <li>Your browser and assistive technology (if applicable)</li>
                  <li>Steps to reproduce the issue</li>
                </ul>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                8. Continuous Improvement
              </h2>
              <p>
                Accessibility is an ongoing process. We regularly review and test our website to identify and fix accessibility issues. We are committed to staying current with accessibility standards and best practices.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                9. Third-Party Content
              </h2>
              <p>
                Some content on our website may be provided by third parties and may not be fully accessible. We work with our partners to encourage accessibility, but we are not responsible for third-party accessibility compliance.
              </p>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AccessibilityPage;
